import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import {
  IPayoutTypes,
  IReferralOffer,
  IReferralStatus,
  getPayoutOptions,
  getReferralOffer,
  getReferralOfferStatus,
  getReferralStatusOptions,
  postPmReferralOffer
} from '~/api/referral';
import { numberRegex } from '~/helpers/helpers';
import { ISelectOption } from '~/helpers/interfaces';
import { FormInputConfig } from '~/types/residentManagement';

const exampleRulesAndEligibility =
  'The referral must result in a lease of at least 12 months. The referring resident must be current and in good standing. The rent credit is issued after the new resident has completed one month of occupancy.';

export interface IPmReferralFormData {
  residentPayout: string;
  referredFriendPayout: string;
  payoutType: string;
  rulesAndEligibility: string;
  startDate: string;
  endDate: string;
  isPublished: boolean;
}

export default (buildingId: string) => {
  const [loader, setLoader] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [referralsTurnedOff, setReferralsTurnedOff] = useState(false);
  const [referralOffer, setReferralOffer] = useState<IReferralOffer | null>(
    null
  );
  const [payoutOptions, setPayoutOptions] = useState<ISelectOption[]>([]);

  const {
    register,
    handleSubmit,
    errors,
    control,
    reset,
    formState: { isDirty }
  } = useForm<IPmReferralFormData>({
    defaultValues: {
      isPublished: true,
      rulesAndEligibility: exampleRulesAndEligibility
    }
  });

  const fetchReferralOffer = async () => {
    try {
      setLoader(true);

      const [offerRes, statusRes, dashboardConfigRes] = await Promise.all([
        getReferralOffer(buildingId),
        getReferralStatusOptions(),
        getReferralOfferStatus(buildingId)
      ]);

      // Missing means never set up; an explicit false means CS turned
      // referrals off, which the backend refuses to override on publish.
      setReferralsTurnedOff(
        (dashboardConfigRes.data as any)?.config?.communityLead === false
      );

      if (offerRes.data) {
        const activeStatus = statusRes.data.find(
          (status: IReferralStatus) => status.name.toLowerCase() === 'active'
        );

        setReferralOffer(offerRes.data);
        reset({
          residentPayout: offerRes.data.residentPayout,
          referredFriendPayout:
            offerRes.data.details?.referredFriendPayout?.toString() ?? '',
          payoutType: offerRes.data.payoutType,
          rulesAndEligibility: offerRes.data.rulesAndEligibility,
          startDate: offerRes.data.startDate?.slice(0, 10),
          endDate: offerRes.data.endDate?.slice(0, 10),
          isPublished: activeStatus
            ? offerRes.data.referralStatus === activeStatus.uuid
            : true
        });
      }

      setLoader(false);
    } catch (e) {
      setLoader(false);
    }
  };

  const fetchPayoutOptions = async () => {
    try {
      const res = await getPayoutOptions();

      setPayoutOptions(
        res.data.map((payoutType: IPayoutTypes) => ({
          value: payoutType.uuid,
          label: payoutType.name
        }))
      );
    } catch (e) {}
  };

  useEffect(() => {
    fetchPayoutOptions();
    fetchReferralOffer();
  }, []);

  const submitPmReferralOffer = async (formData: IPmReferralFormData) => {
    try {
      setLoader(true);
      setSaveError('');

      await postPmReferralOffer({ buildingId, ...formData });

      toast('Referral program saved successfully');
      await fetchReferralOffer();

      setLoader(false);
    } catch (e) {
      // The global handler only toasts briefly; keep the reason on the form.
      setSaveError((e as { message?: string })?.message ?? '');
      setLoader(false);
    }
  };

  const pmReferralFormConfig: FormInputConfig[] = [
    {
      label: 'Reward For The Current Resident (Referrer)',
      id: 'residentPayout',
      pattern: numberRegex(),
      patternErrorMsg: 'Oops! Please enter a valid number',
      isRequired: true,
      placeholder: 'e.g. 1000'
    },
    {
      label: 'Reward For The Referred Friend (Optional)',
      id: 'referredFriendPayout',
      isRequired: false,
      pattern: numberRegex(),
      patternErrorMsg: 'Oops! Please enter a valid number',
      placeholder: 'e.g. 500. Leave empty if only the referrer is rewarded'
    },
    {
      label: 'How The Reward Is Paid',
      id: 'payoutType',
      inputType: 'select',
      selectOptions: payoutOptions,
      isRequired: true,
      placeholder: 'Select how the reward is paid'
    },
    {
      label: 'Rules & Eligibility',
      id: 'rulesAndEligibility',
      inputType: 'textarea',
      isRequired: true,
      placeholder: 'Example terms. Adapt them to your property rules.'
    },
    {
      label: 'Start Date',
      id: 'startDate',
      inputType: 'date',
      isRequired: true
    },
    {
      label: 'End Date',
      id: 'endDate',
      inputType: 'date',
      isRequired: true
    },
    {
      label: 'Published',
      id: 'isPublished',
      inputType: 'switch'
    }
  ];

  return {
    loader,
    referralOffer,
    pmReferralFormConfig,
    register,
    handleSubmit,
    errors,
    control,
    isDirty,
    saveError,
    referralsTurnedOff,
    submitPmReferralOffer
  };
};
